/**
 * The visitor's link key — the one credential the visitor's own page has.
 *
 * The invitation links to `/v/<customerId>#key=<key>`. The key is in the
 * FRAGMENT because a fragment is never sent to a server: not to ours in the
 * request line, not to a proxy's access log, not in a `Referer`. `?key=` is
 * read only when there is no fragment key (a mail client that dropped it).
 *
 * The key is read ONCE, kept in memory only, and taken out of the address bar
 * straight away with `history.replaceState`:
 *
 *   • never localStorage / sessionStorage — anyone later at the same browser
 *     would hold the visit;
 *   • never analytics, never a log — it is a bearer credential;
 *   • out of the address bar before anything reads the URL. `main.ts` calls
 *     `captureVisitorLinkKey()` BEFORE Angular bootstraps, so the router's URL
 *     tree, the first pageview and every NavigationEnd listener only ever see
 *     the clean URL. The page calls it again on init, for an in-app navigation.
 *
 * The consequence, which the page states: after a refresh the key is gone and
 * the visitor opens the link again. That is the trade for not storing it.
 *
 * `readVisitorLink` / `isVisitorPagePath` are pure. No Angular imports, so the
 * spec runs under bare vitest as well as `ng test`.
 */

export const VISITOR_KEY_PARAM = 'key';

/** Longer than any key the API mints (32 hex characters); anything past this is not one. */
const KEY_MAX = 512;

export interface VisitorLinkRead {
  /** The key, or null when the URL carries none (or an empty one). */
  key: string | null;
  /**
   * The same-origin URL (path, query, fragment) with every `key` removed, or
   * null when there was nothing to remove — then the address bar is left alone.
   */
  cleanHref: string | null;
}

/** `/v/<customerId>` — and the harness's copy under `/preview/visitors`. */
const VISITOR_PAGE_PATH = /^\/(?:preview\/visitors\/)?v\/[^/?#]+\/?$/;

export function isVisitorPagePath(pathname: string): boolean {
  return VISITOR_PAGE_PATH.test(pathname);
}

/**
 * Split `a=1&key=abc` into its parts. Decoded with `decodeURIComponent`, not
 * URLSearchParams, which would turn a `+` in a key into a space.
 */
function splitParams(raw: string): { name: string; value: string; part: string }[] {
  return raw
    .split('&')
    .filter((part) => part.length > 0)
    .map((part) => {
      const eq = part.indexOf('=');
      const name = eq < 0 ? part : part.slice(0, eq);
      const value = eq < 0 ? '' : part.slice(eq + 1);
      return { name: safeDecode(name), value: safeDecode(value), part };
    });
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    // A malformed escape: keep it as typed rather than throw on a visitor's URL.
    return value;
  }
}

function cleanKey(value: string | undefined): string | null {
  const key = (value ?? '').trim();
  return key && key.length <= KEY_MAX ? key : null;
}

/**
 * Read the key out of a URL and say what the URL should become without it.
 * Every `key` is removed from BOTH the fragment and the query, whichever one
 * was used, so no copy of the credential is left in the address bar.
 */
export function readVisitorLink(href: string): VisitorLinkRead {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return { key: null, cleanHref: null };
  }
  const fragment = splitParams(url.hash.replace(/^#/, ''));
  const query = splitParams(url.search.replace(/^\?/, ''));
  const isKey = (p: { name: string }) => p.name === VISITOR_KEY_PARAM;

  const fromFragment = cleanKey(fragment.find(isKey)?.value);
  const fromQuery = cleanKey(query.find(isKey)?.value);
  const hadKey = fragment.some(isKey) || query.some(isKey);
  if (!hadKey) return { key: null, cleanHref: null };

  const restQuery = query.filter((p) => !isKey(p)).map((p) => p.part).join('&');
  const restFragment = fragment.filter((p) => !isKey(p)).map((p) => p.part).join('&');
  const cleanHref = `${url.pathname}${restQuery ? `?${restQuery}` : ''}${restFragment ? `#${restFragment}` : ''}`;
  return { key: fromFragment ?? fromQuery, cleanHref };
}

// ── The one place the key is held (memory only) ─────────────────────────────

let held: string | null = null;

/**
 * Read the key from the current URL, hold it, and scrub it from the address
 * bar. Safe to call more than once: a URL with no key leaves the held key as
 * it is. `history.state` is kept, because Angular's router keeps its
 * navigation id there.
 */
export function captureVisitorLinkKey(
  loc: Pick<Location, 'href'> = window.location,
  hist: Pick<History, 'replaceState' | 'state'> = window.history,
): void {
  const { key, cleanHref } = readVisitorLink(loc.href);
  if (cleanHref !== null) hist.replaceState(hist.state, '', cleanHref);
  if (key) held = key;
}

/** Hand the held key over, once. The page's service keeps it from then on. */
export function takeVisitorLinkKey(): string | null {
  const key = held;
  held = null;
  return key;
}
